import AdyenTerminalComms, { CardAcquisitionReference } from '@baemingo/adyen-terminal-comms';
import { CardDetails } from '~reducers';
import * as actions from '~actions';
import { DatadogLogger } from '../useLogger';
import { SocketAPI } from '../../../Components/SocketManager';

interface Props {
  reference?: string;
  terminalInstance: AdyenTerminalComms;
  logger: DatadogLogger;
  amount: number;
  transactionId: string;
  enableTipping: boolean;
  customServiceId: string;
  /**
   * From a preceding useAdyen.requestCard. Charges the card already read by the
   * terminal, so the shopper does not present it again.
   */
  cardAcquisitionReference?: CardAcquisitionReference;
  onComplete: (result: { transactionId: string; tip: number; cardDetails?: CardDetails }) => void;
  onFail: (reason: string) => void;
  dispatch: ({
    type,
    payment,
  }: {
    type: typeof actions.UPDATE_POS_PAYMENT;
    payment: {
      id: string;
      hasConnectionIssues: boolean;
    };
  }) => void;
  socket?: SocketAPI;
  useSocket?: boolean;
}

const pay = ({
  reference,
  terminalInstance,
  logger,
  enableTipping,
  amount,
  transactionId,
  customServiceId,
  cardAcquisitionReference,
  onComplete,
  onFail,
  dispatch,
  socket,
  useSocket,
}: Props) => {
  logger.info('Starting Adyen payment: ' + amount / 100, {
    paymentId: reference,
    terminalId: terminalInstance.senderPoiid,
    serviceId: customServiceId,
  });

  /**
   * If request ended but we havent gotten a payment response yet
   */
  let failCount = 0;

  let hasEnded = false;

  let hasPendingRequest = false;

  let id: ReturnType<typeof setInterval>;

  function pollForResult(saleId: string, serviceId: string) {
    if (hasPendingRequest) {
      logger.info('Already have a pending request - skipping this poll', {
        paymentId: reference,
        terminalId: terminalInstance.senderPoiid,
      });
      return;
    }

    hasPendingRequest = true;

    logger.info('Polling for payment status...', {
      paymentId: reference,
      terminalId: terminalInstance.senderPoiid,
      saleId,
      serviceId,
    });

    /**
     * Poll for payment info every 1.5 seconds
     */
    terminalInstance
      .transactionStatus(saleId, customServiceId || serviceId)
      .then((response) => {
        logger.info('Poll Response', {
          paymentId: reference,
          terminalId: terminalInstance.senderPoiid,
          response,
        });

        if (hasEnded) {
          logger.info('Already ended - ignoring the response', {
            paymentId: reference,
          });
          return;
        }

        if (!response.success || response.status === 'Offline') {
          if (failCount === 0) {
            logger.warn('Payment connection issues', {
              paymentId: reference,
              terminalId: terminalInstance.senderPoiid,
            });

            dispatch({
              type: actions.UPDATE_POS_PAYMENT,
              payment: {
                id: reference!,
                hasConnectionIssues: true,
              },
            });
          }

          failCount++;

          logger.warn(`Transaction status failed. ${response?.message}`, {
            paymentId: reference,
            terminalId: terminalInstance.senderPoiid,
            payload: response.payload,
            failCount,
          });

          const payload = response.payload;
          if (typeof payload === 'string' && payload.includes('message=Message%20not%20found')) {
            onFail('Payment not found');
            hasEnded = true;

            logger.warn('Payment ended due to not found', {
              paymentId: reference,
              terminalId: terminalInstance.senderPoiid,
            });
            clearInterval(id);
            return;
          }

          if (failCount > 120) {
            onFail('Timeout');
            hasEnded = true;

            logger.warn('Payment ended due to Timeout / failcount', {
              paymentId: reference,
              terminalId: terminalInstance.senderPoiid,
            });
            clearInterval(id);
          }

          return;
        }

        // Successful response - reset fail count
        if (failCount) {
          logger.info('Recovered after ' + failCount + 'attempts');

          dispatch({
            type: actions.UPDATE_POS_PAYMENT,
            payment: {
              id: reference!,
              hasConnectionIssues: false,
            },
          });
        }
        failCount = 0;

        if (response.status === 'InProgress') {
          logger.info('Payment in progress...', {
            paymentId: reference,
          });
          return;
        }

        // We are done - end the polling
        clearInterval(id);
        hasEnded = true;

        /**
         * The payment did not go through
         */
        if (response.status === 'Failure') {
          logger.info(`Payment did not go through: ${response.status}`, {
            paymentId: reference,
            terminalId: terminalInstance.senderPoiid,
            payload: response.payload,
            response: response,
          });
          onFail(response.message!);
          return;
        }

        /**
         * The payment DID go through
         */
        logger.info('Payment completed after extended duration', {
          paymentId: reference,
          terminalId: terminalInstance.senderPoiid,
        });

        onComplete({
          transactionId: response.payload!.POIData.POITransactionID.TransactionID,
          tip: response.completedPaymentPayload?.tip || 0,
          cardDetails: response.completedPaymentPayload?.cardDetails,
        });
      })
      .catch((e) => {
        console.error(e);
        logger.warn(`Payment error: ${e.message}`, {
          paymentId: reference,
          terminalId: terminalInstance.senderPoiid,
        });
      })
      .finally(() => {
        hasPendingRequest = false;
      });
  }

  if (useSocket && socket) {
    if (cardAcquisitionReference) {
      // START_PAYMENT_REQUEST has no field for it, so the managed terminal will
      // start a fresh payment and ask the shopper to present their card again.
      logger.warn('Card acquisition reference dropped: the websocket path cannot carry it', {
        paymentId: reference,
        terminalId: terminalInstance.senderPoiid,
      });
    }

    logger.info('Sending payment to managed terminal via websocket', {
      terminalId: terminalInstance.senderPoiid,
      customServiceId,
      amount: amount!,
    });

    socket.send.sendToTerminal(terminalInstance.senderPoiid!, {
      type: 'START_PAYMENT_REQUEST',
      terminalId: terminalInstance.senderPoiid!,
      serviceId: customServiceId,
      amount: amount!,
      transactionId,
    });

    setTimeout(() => {
      const poll = () => pollForResult('device', customServiceId);
      id = setInterval(poll, 1500);
    }, 1000);
  } else {
    const handleResponse = terminalInstance
      .pay(amount, transactionId, enableTipping, customServiceId, { cardAcquisitionReference })
      .then(({ success, status, message, transactionId, tip, saleId, serviceId, cardDetails }) => {
        logger.info('Adyen payment response', {
          response: {
            paymentId: reference,
            terminalId: terminalInstance.senderPoiid,
            success,
            _status: status,
            _message: message,
            transactionId,
            tip,
            saleId,
            serviceId,
            cardDetails,
          },
        });

        if (!success) {
          logger.error(`Payment Error: ${message}`, {
            paymentId: reference,
            terminalId: terminalInstance.senderPoiid,
          });

          onFail(message);
          return;
        }

        if (status === 'Paid') {
          logger.info('Payment completed', {
            paymentId: reference,
            terminalId: terminalInstance.senderPoiid,
            cardDetails,
          });

          onComplete({
            transactionId,
            tip: tip!,
            cardDetails: cardDetails,
          });

          return;
        }

        if (status === 'Failure') {
          logger.warn(`Payment did not go through: ${message}`, {
            paymentId: reference,
            terminalId: terminalInstance.senderPoiid,
          });

          onFail(message);
          return;
        }

        logger.info(
          `Payment did not complete in first request. Heading into polling. ${message}`,
          {
            paymentId: reference,
            terminalId: terminalInstance.senderPoiid,
          },
        );

        logger.info('Starting to poll for payment status...');

        const poll = () => pollForResult(saleId!, serviceId!);
        id = setInterval(poll, 1500);
      });
  }
};

export default pay;
